import type { ProposalDefinition } from '@/types/project-command';
import type { DeliveryPreference } from '@/types/delivery-preference';
import type {
  MilestoneView,
  ProjectView,
  WeekWindow,
} from '@/types/current-project';

export function projectDateLabel(seconds: number) {
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime())
    ? `${seconds} Unix seconds`
    : date.toLocaleString();
}

export function deliveryPreferenceLabel(delivery?: DeliveryPreference | null) {
  if (!delivery) return 'Not specified';
  if (delivery.preference === 'SpecificDate')
    return new Date(delivery.date + 'T00:00:00').toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  return {
    WithinOneMonth: 'Within 1 month',
    OneToThreeMonths: '1-3 months from start',
    ThreeToSixMonths: '3-6 months from start',
  }[delivery.preference];
}

export const PROJECT_STATUS_LABELS = [
  'Cancelled',
  'Dispute open',
  'Completed',
  'Execution in progress',
  'Proposal awaiting approval',
  'Awaiting planning quote',
  'Planning quote ready',
  'Planning in progress',
  'Planning delivery ready',
  'Planning complete',
] as const;
export function executionMilestones(project: ProjectView): MilestoneView[] {
  return project.proposals
    .filter((proposal) => proposal.status === 'Approved')
    .flatMap((proposal) => proposal.milestones);
}
export function projectStatus(
  project: ProjectView
): (typeof PROJECT_STATUS_LABELS)[number] {
  if (project.cancelled) return 'Cancelled';
  if (
    project.activeDisputeId ||
    project.planning.status === 'Disputed' ||
    executionMilestones(project).some(
      (milestone) => milestone.status === 'Disputed'
    )
  )
    return 'Dispute open';
  if (project.completed) return 'Completed';
  if (project.proposals.some((proposal) => proposal.status === 'Approved'))
    return 'Execution in progress';
  if (
    project.proposals.some((proposal) => proposal.status === 'PendingApproval')
  )
    return 'Proposal awaiting approval';
  const labels = {
    AwaitingQuote: 'Awaiting planning quote',
    Quoted: 'Planning quote ready',
    Accepted: 'Planning in progress',
    Delivered: 'Planning delivery ready',
    Completed: 'Planning complete',
    Disputed: 'Dispute open',
  } as const;
  return labels[project.planning.status];
}
export function projectProgress(project: ProjectView): number | null {
  const milestones = executionMilestones(project);
  if (!milestones.length) return null;
  return Math.round(
    (milestones.filter((milestone) => milestone.status === 'Completed').length /
      milestones.length) *
      100
  );
}
export function milestoneBudget(milestone: MilestoneView): string {
  return milestone.definition.requirements
    .reduce(
      (total, requirement) => total + BigInt(requirement.budget),
      BigInt(milestone.definition.coordinatorFee)
    )
    .toString();
}
export function weekWindowLabel(window: WeekWindow): string {
  const label = (week: WeekWindow['start']) =>
    week.isoYear + ' W' + String(week.week).padStart(2, '0');
  return label(window.start) + ' – ' + label(window.end);
}
export function participation(
  project: ProjectView,
  account: string | undefined
): string[] {
  if (!account) return [];
  const roles: string[] = [];
  if (project.client === account) roles.push('Client');
  if (project.coordinator === account) roles.push('Coordinator');
  if (
    project.proposals.some((proposal) =>
      proposal.milestones.some((milestone) =>
        milestone.assignments.some(
          (assignment) => assignment.worker === account
        )
      )
    )
  )
    roles.push('Assigned worker');
  if (
    project.proposals.some((proposal) =>
      proposal.milestones.some((milestone) =>
        milestone.taskStorage.tasks.some((task) =>
          task.task.assignees.includes(account)
        )
      )
    )
  )
    roles.push('Task assignee');
  return roles;
}

export function proposalCost(definition: ProposalDefinition) {
  return definition.milestones
    .reduce(
      (sum, m) =>
        sum +
        BigInt(m.coordinatorFee) +
        m.requirements.reduce((total, r) => total + BigInt(r.budget), 0n),
      0n
    )
    .toString();
}
